import React, { useEffect, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Placeholder from "components/atoms/Placeholder";
import Tooltip from "components/atoms/Tooltip";
import Button from "components/atoms/button/Button";
import GeneralText from "components/atoms/text/GeneralText";
import useToast from "components/atoms/toast/useToast";

import AbstractModal from "components/page/modal/AbstractModal";
import AbstractModalFooter from "components/page/modal/AbstractModalFooter";
import TagEditModal from "components/page/modal/TagEditModal";

import { useTagCreate, useTagRecommendation, useTagReferenceCreate, useTags } from "hooks/queries/useBibliographies";

const Container = styled.div`
    width: 100%;
`;

const MessageLine = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 24px 0 0 0;
    width: 100%;
`;

const ContentsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 16px 0 40px 0;
    width: 100%;
    height: 320px;
`;

const NormalView = styled.div`
    display: block;
    padding: 8px;
    width: 100%;
    height: 320px;
    max-height: 320px;
    background-color: var(--color-Base1);
    border-radius: 4px;
    overflow: auto;
`;

const BlankView = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const TagListWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
`;

const TagItem = styled.div`
    margin: 0 4px 4px 0;
    padding: 6px;
    max-width: 240px;
    height: 28px;
    background-color: ${(props) => (props.selected ? "var(--color-TagActive)" : "var(--color-Tag)")};
    border: solid 1px transparent;
    border-radius: 2px;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: 120ms ease-in;
    display: flex;
    align-items: center;

    &:hover {
        border: solid 1px var(--color-Key);
    }
`;

const RecommendedTagItem = styled(TagItem)``;

const TagAddButtonWrap = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 16px 0 0 0;
`;

function ReferenceTagEditModal(props) {
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [tags, setTags] = useState([]);
    const [showTagAddModal, setShowTagAddModal] = useState(false);

    const tagCreate = useTagCreate();
    const tagReferenceCreate = useTagReferenceCreate();

    const tagQuery = useTags();

    const { setToast } = useToast();

    useEffect(() => {
        if (props.selectedTagIds && selectedTagIds.length === 0) {
            setSelectedTagIds(props.selectedTagIds);
        }
    }, [props.selectedTagIds]);

    useEffect(() => {
        if (tagQuery.data) {
            setTags(tagQuery.data);
        }
    }, [tagQuery.data]);

    const handleCheck = (tagId) => {
        if (selectedTagIds.includes(tagId)) {
            setSelectedTagIds(selectedTagIds.filter((selectedTag) => selectedTag !== tagId));
        } else {
            setSelectedTagIds([...selectedTagIds, tagId]);
        }
    };

    const applySelectedTag = () => {
        tagReferenceCreate.mutate(
            {
                reference_ids: [props.referenceId],
                tag_ids: selectedTagIds,
                recommended_tags: recommendedTags.filter((tag) => tag.selected).map((tag) => tag.name),
                delete_old_tag: true,
            },
            {
                onSuccess: () => {
                    props.exitModal();
                    setToast("선택한 태그가 연결되었습니다.", "info");
                },
                onError: (error) => {
                    setToast("태그 연결에 실패했습니다. 잠시후 시도해주세요.", "error");
                },
            },
        );
    };

    const [recommendedTags, setRecommendedTags] = useState([]);
    const tagRecommendation = useTagRecommendation();

    useEffect(() => {
        if (props.referenceFile) {
            tagRecommendation.mutate(
                { reference_id: props.referenceId },
                {
                    onSuccess: (data, variables, context) => {
                        setRecommendedTags(
                            data.tag_list.map((tag_name) => {
                                return {
                                    name: tag_name,
                                    selected: false,
                                };
                            }),
                        );
                    },
                    onError: (error, variables, context) => {
                        console.log("recommendation error");
                        console.log(error);
                    },
                },
            );
        }
    }, [props.referenceFile]);

    return (
        <Container>
            <MessageLine>
                <GeneralText size={"regular"}>참고문헌에 연결할 태그를 선택해주세요.</GeneralText>
            </MessageLine>

            <TagAddButtonWrap>
                <Button
                    width={"100px"}
                    height={"24px"}
                    bgColor={"var(--color-Button1)"}
                    hoverBgColor={"var(--color-ButtonHover1)"}
                    fontColor={"var(--color-White)"}
                    buttonText={"새 태그"}
                    onClick={(e) => setShowTagAddModal(true)}
                >
                    <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                </Button>
            </TagAddButtonWrap>

            <ContentsWrapper>
                <NormalView>
                    {tags.length > 0 || props.referenceFile ? (
                        <TagListWrap>
                            {tagRecommendation.isLoading && (
                                <>
                                    {["70px"].map((width, index) => (
                                        <Tooltip message={"PDF를 분석 중입니다."} key={"tooltip" + index}>
                                            <RecommendedTagItem key={index}>
                                                <Placeholder
                                                    style={{ width: width, height: "15px", borderRadius: "1px" }}
                                                />
                                            </RecommendedTagItem>
                                        </Tooltip>
                                    ))}
                                </>
                            )}
                            {props.selectedTagIds.map((tagId, index) => {
                                const tag = tags.find((tag) => tag.id === tagId);
                                if (!tag) return null;

                                return (
                                    <Tooltip
                                        message={
                                            selectedTagIds.includes(tag.id)
                                                ? "참고문헌에 연결된 태그입니다."
                                                : "참고문헌에 연결되지 않은 태그입니다."
                                        }
                                        key={"tooltip" + index}
                                    >
                                        <TagItem
                                            key={index}
                                            selected={selectedTagIds.includes(tagId)}
                                            onClick={(e) => handleCheck(tagId)}
                                        >
                                            {recommendedTags.find(
                                                (recommendedTag) => recommendedTag.name === tag.name,
                                            ) && (
                                                <Icon
                                                    name={"ai"}
                                                    size={"12"}
                                                    color="var(--color-Black)"
                                                    style={{ marginRight: "4px" }}
                                                />
                                            )}
                                            <GeneralText ellipsis size={"small"} color={"var(--color-Black)"}>
                                                {tag.name}
                                            </GeneralText>
                                        </TagItem>
                                    </Tooltip>
                                );
                            })}
                            {recommendedTags.length > 0 && (
                                <>
                                    {recommendedTags
                                        .filter(
                                            (recommendedTag) =>
                                                !props.selectedTagIds.some((id) => {
                                                    const tag = tags.find((tag) => tag.id === id);
                                                    return tag && tag.name === recommendedTag.name;
                                                }),
                                        )
                                        .map((tag, index) => (
                                            <Tooltip
                                                message={
                                                    tag.selected
                                                        ? "참고문헌에 연결된 태그입니다."
                                                        : "PDF를 분석해 추천된 태그입니다."
                                                }
                                                key={"tooltip" + index}
                                            >
                                                <RecommendedTagItem
                                                    key={JSON.stringify(tag)}
                                                    selected={tag.selected}
                                                    onClick={(e) => {
                                                        const newRecommendedTags = [...recommendedTags];
                                                        const tagIndex = newRecommendedTags.findIndex(
                                                            (t) => t.name === tag.name,
                                                        );
                                                        if (tagIndex !== -1) {
                                                            newRecommendedTags[tagIndex].selected =
                                                                !newRecommendedTags[tagIndex].selected;
                                                        }

                                                        setRecommendedTags(newRecommendedTags);
                                                    }}
                                                >
                                                    <Icon
                                                        name={"ai"}
                                                        size={"12"}
                                                        color="var(--color-Black)"
                                                        style={{ marginRight: "4px" }}
                                                    />
                                                    <GeneralText ellipsis size={"small"} color={"var(--color-Black)"}>
                                                        {tag.name}
                                                    </GeneralText>
                                                </RecommendedTagItem>
                                            </Tooltip>
                                        ))}
                                </>
                            )}
                            {tags
                                .filter(
                                    (tag) =>
                                        !props.selectedTagIds.includes(tag.id) &&
                                        !recommendedTags.some((recommendedTag) => recommendedTag.name === tag.name),
                                )
                                .map((tag, index) => (
                                    <Tooltip
                                        message={
                                            selectedTagIds.includes(tag.id)
                                                ? "참고문헌에 연결된 태그입니다."
                                                : "참고문헌에 연결되지 않은 태그입니다."
                                        }
                                        key={"tooltip" + index}
                                    >
                                        <TagItem
                                            key={index}
                                            selected={selectedTagIds.includes(tag.id)}
                                            onClick={(e) => handleCheck(tag.id)}
                                        >
                                            <GeneralText ellipsis size={"small"} color={"var(--color-Black)"}>
                                                {tag.name}
                                            </GeneralText>
                                        </TagItem>
                                    </Tooltip>
                                ))}
                        </TagListWrap>
                    ) : (
                        <BlankView>
                            <GeneralText size={"small"} textAlign={"center"} color={"var(--color-DisabledText)"}>
                                아직 태그가 없습니다.
                            </GeneralText>
                        </BlankView>
                    )}
                </NormalView>
            </ContentsWrapper>
            <AbstractModalFooter
                leftBtnText={"취소"}
                rightBtnText={"적용"}
                leftOnClick={props.exitModal}
                rightOnClick={(e) => applySelectedTag()}
            />
            {showTagAddModal && (
                <AbstractModal modalTitle={"Create Tag"} width={480} exitModal={(e) => setShowTagAddModal(false)}>
                    <TagEditModal
                        exitModal={(e) => setShowTagAddModal(false)}
                        item={showTagAddModal}
                        mutation={tagCreate}
                        onSuccess={(added) => handleCheck(added.id)}
                    />
                </AbstractModal>
            )}
        </Container>
    );
}

export default ReferenceTagEditModal;
